import { BehaviorSubject } from "rxjs";
import { map, filter } from "rxjs/operators";
// Removed appDirLookupMap import - no longer needed with Secrets Manager approach
import { getMappedProject } from "./consts/projects-map";
/**
 * Configuration Service - Static class for managing application configuration.
 *
 * ═══════════════════════════════════════════════════════════════════════════════
 * OVERVIEW
 * ═══════════════════════════════════════════════════════════════════════════════
 * This class stores and provides access to application configuration loaded from
 * AWS Secrets Manager. It acts as a central store for all config values (API URLs,
 * feature flags, environment settings, etc.).
 *
 * ═══════════════════════════════════════════════════════════════════════════════
 * HOW IT WORKS (New Pre-Bootstrap Approach)
 * ═══════════════════════════════════════════════════════════════════════════════
 * 1. In main.ts, authenticateAndLoadConfigsBeforeBootstrap() fetches configs
 *    from AWS Secrets Manager after Okta authentication
 * 2. It calls Configuration.setConfig(configs) to store configs
 * 3. Angular services/components use Configuration.get() to retrieve values
 *
 * ═══════════════════════════════════════════════════════════════════════════════
 * METHODS SUMMARY
 * ═══════════════════════════════════════════════════════════════════════════════
 *
 * ✅ ACTIVE METHODS (Used with new implementation):
 * - setActiveAppName(appName)    : Set current app name (called in pre-bootstrap)
 * - getActiveAppName()           : Get current app name
 * - setConfig(data)              : Store config object (called in pre-bootstrap)
 * - get(key)                     : Get config value by key (dot notation supported)
 * - getAsync(key)                : Get config value asynchronously (waits for load)
 * - getConfig()                  : Get entire config object
 * - getConfigByStringKey()       : Helper for nested key lookup
 * - updateNewRelicConfig()       : Initialize New Relic with config values
 * - isProductionEquivalentEnvironment() : Check if running in prod/uat/stg
 *
 * ⚠️ DEPRECATED METHODS (Old config.json approach - kept for backward compatibility):
 * - load()                       : Load from local config.json files
 * - getDefaultConfig()           : Get default config from config.json
 * - getSerializedConfig()        : Merge configs (used by load())
 *
 * ═══════════════════════════════════════════════════════════════════════════════
 * USAGE EXAMPLES
 * ═══════════════════════════════════════════════════════════════════════════════
 *
 * // Get a simple value
 * const env = Configuration.get('environment'); // 'dev'
 *
 * // Get nested value using dot notation
 * const apiUrl = Configuration.get('api.fulfillment-graphql-api');
 *
 * // Get value asynchronously (waits for config to be loaded)
 * Configuration.getAsync('launchDarkly').subscribe(ldConfig => { ... });
 *
 * // Get entire config
 * const allConfigs = Configuration.getConfig();
 *
 * // Check environment
 * if (Configuration.isProductionEquivalentEnvironment()) { ... }
 */
export class Configuration {
    // ═══════════════════════════════════════════════════════════════════════════
    // STATIC PROPERTIES
    // ═══════════════════════════════════════════════════════════════════════════
    /** Check if running on localhost */
    static { this.isRunningInLocalhost = document.URL.toString().includes("localhost"); }
    // ═══════════════════════════════════════════════════════════════════════════
    // OBSERVABLES - For async config access
    // ═══════════════════════════════════════════════════════════════════════════
    /** Emits true when config has been set via setConfig() */
    static { this.hasSetSubject = new BehaviorSubject(false); }
    // Removed defaultConfig - no longer needed with Secrets Manager approach
    // ═══════════════════════════════════════════════════════════════════════════
    // ACTIVE METHODS - Used with new pre-bootstrap implementation
    // ═══════════════════════════════════════════════════════════════════════════
    /**
     * Sets the active application name.
     * Called in main.ts during pre-bootstrap config loading.
     * @param appName - Application name (e.g., 'fulfillment', 'c3')
     */
    static setActiveAppName(appName) {
        const mappedAppName = getMappedProject[appName] || appName;
        console.log("mapped project.. ", mappedAppName);
        this.activeAppName = mappedAppName;
    }
    /**
     * Gets the active application name.
     * Used by services to identify the current app context.
     */
    static getActiveAppName() {
        return this.activeAppName;
    }
    /**
     * Gets a config value by key. Supports dot notation for nested values.
     * @param key - Config key (e.g., 'environment' or 'api.fulfillment-graphql-api')
     * @returns Config value or null if not found
     *
     * @example
     * Configuration.get('environment')              // 'dev'
     * Configuration.get('api.fulfillment-graphql-api') // 'https://...'
     * Configuration.get('launchDarkly.clientId')    // '63034db...'
     */
    static get(key) {
        const result = Configuration.getConfigByStringKey(key.split("."), this.config);
        return this.config && result;
    }
    /**
     * Gets a config value asynchronously. Waits for config to be loaded first.
     * Use this when you're not sure if config is loaded yet.
     * @param key - Config key (supports dot notation)
     * @returns Observable that emits the config value
     */
    static getAsync(key) {
        return this.hasSetSubject.pipe(filter((hasBeenSet) => !!hasBeenSet), map(() => {
            const keys = key.split(".");
            const result = Configuration.getConfigByStringKey(keys, this.config);
            return result;
        }));
    }
    /**
     * Stores the configuration object.
     * Called during pre-bootstrap after fetching from Secrets Manager.
     * @param data - Configuration object from Secrets Manager
     */
    static setConfig(data) {
        this.config = data;
        this.hasSetSubject.next(true);
    }
    /**
     * Gets the entire configuration object.
     * @returns Complete config object
     */
    static getConfig() {
        return this.config;
    }
    /**
     * Helper method to traverse nested config using key array.
     * Used internally by get() and getAsync().
     * @param keys - Array of keys (e.g., ['api', 'fulfillment-graphql-api'])
     * @param config - Config object to search
     * @returns Value at the nested path or null
     */
    static getConfigByStringKey(keys, config) {
        if (keys.length === 0 || !config || !config[keys[0]]) {
            return null;
        }
        if (keys.length === 1) {
            return config[keys[0]];
        }
        else if (keys.length > 1 && !(config[keys[0]] instanceof Object)) {
            return null;
        }
        const nextConfig = config[keys[0]];
        keys.shift();
        return Configuration.getConfigByStringKey(keys, nextConfig);
    }
    /**
     * Initializes New Relic with configuration values.
     * Sets up window.NREUM with license key, application ID, etc.
     * @param newRelic - Optional New Relic overrides
     * @returns true if successfully configured, false otherwise
     */
    static updateNewRelicConfig(newRelic) {
        const config = this.getConfig();
        const newRelicConfig = config?.["newRelic"];
        if (!newRelicConfig) {
            return false;
        }
        const windowWithNR = window;
        const NREUM = (windowWithNR.NREUM = windowWithNR.NREUM || {});
        // Prefer values from `newRelic`, fallback to `newRelicConfig`
        const { licenseKey = newRelicConfig.licenseKey, applicationID = newRelicConfig.applicationID, accountID = newRelicConfig.accountID, trustKey = newRelicConfig.trustKey, agentID = newRelicConfig.agentID } = newRelic ?? {};
        if (!licenseKey || !applicationID) {
            console.warn("⚠️ Missing New Relic licenseKey or applicationID in configuration.");
            return false;
        }
        NREUM.init = newRelicConfig.init;
        NREUM.loader_config = {
            ...(newRelicConfig.loader_config ?? {}),
            licenseKey,
            applicationID,
            accountID,
            trustKey,
            agentID
        };
        NREUM.info = {
            ...newRelicConfig.info,
            licenseKey,
            applicationID
        };
        console.log("🚀🚀--NREUM Updated.", NREUM);
        console.log("✅ New Relic configurations set in window.NREUM.");
        return true;
    }
    /**
     * Checks if running in a production-equivalent environment.
     * @returns true if environment is 'prod', 'uat', or 'stg'
     */
    static isProductionEquivalentEnvironment() {
        const environment = this.config?.["environment"];
        return this.config && typeof environment === 'string' && ["prod", "uat", "stg"].includes(environment);
    }
}
//# sourceMappingURL=configuration.js.map